import React, { Fragment, PureComponent } from 'react';
import LazyHydrate from 'react-lazy-hydration';
import PropTypes from 'prop-types';
import { JsonLd } from 'react-schemaorg';
import each from 'lodash/each';
import sortBy from 'lodash/sortBy';
import map from 'lodash/map';
import get from 'lodash/get';
import filter from 'lodash/filter';
import Bem from 'bemHelper';
import { I18nHoc } from 'helpers/i18n';
import urlShortener from 'helpers/urlShortener';
import formatDate from 'helpers/formatDate';
import { numDescOnlyText } from 'helpers/numDesc';
import beautifyNumber from 'helpers/beautifyNumber';
import absoluteUrl from 'helpers/absoluteUrl';
import seoPreload, { seoDecorator } from 'api/seo';
import DataProvider from 'api/data-provider';
import Dict from 'api/data-dict';
import preload from 'api/data-preload';
import { simpleCheck } from 'api/data-check';
import GeoLandings from 'subsys/geo-landings';
import { FavoriteWrapper } from 'subsys/favorites';
import { marketStatEvents, mstatDecorator } from 'subsys/market-stat';
import Counter from 'subsys/counter';
import { InputListBox } from 'containers/form';
import { Leaflet, Maps, BaseLayersUser, InfoLayer } from 'containers/maps';
import contentBlock, { blockPreloader } from 'containers/content-block';
import Container from 'components/container';
import Row, { Col } from 'components/row';
import TabJSControl from 'components/tab-js-control';
import Image, { Thumb, OpenGraphImage } from 'components/image';
import CollapseLines from 'components/clamp-lines';
import Link from 'components/link';
import { AbstractQueryPage } from 'components/view-map-search';
import Breadcrumbs from 'components/breadcrumbs';
import { SeoText } from 'components/layouts';
import { HideTelephoneSeo } from 'components/hide-telephone';
import ToolTip from 'components/tooltip';
import Icon, { iconTypes } from 'components/icon';

import linksBild from 'src-sites/bild/links';

import TileCard from '../section-projects/tile-card';
import ListCard from '../section-projects/list-card';
import renderGeneralMap from '../section-projects/render-map';
import {
  getBigMarkerType,
  mapTile,
  sizeMap,
  mapLegendView,
  mapMarkerRenderer,
  complexMapTile,
  outsideMapCard
} from '../section-projects/common';
import { ordersLC } from '../section-projects/orders';
import { getHousesCounter } from '../section-project/render-common';
import marker from '../assets/map-marker';
import blocksConfig from '../blocks/config';
import BottomBlockWrapper from '../blocks/bottomBlockWrapper';

import translates from './developer-i18n';

import 'styles/bild/section-developer/_page-developer.scss';

const bemClasses = new Bem('developer');
const classesSearch = new Bem('pageSearch');

@preload((routeProps) => {
  const { location } = routeProps;
  const { landing } = routeProps.params;
  const { query = {} } = location;

  const localityInfo = landing.params.projects ? {
    locality: [DataProvider.preload, {
      url: 'landing/location-info',
      cache: 60 * 24,
      query: { id: landing.params.projects.locality },
      preloaderCacheId: 'locationInfolocality',
      requestClientCache: 'locality'
    }] } : {};

  return {
    ...blockPreloader({
      project: 'bild',
      entity: 'developerPage'
    }, blocksConfig, []),
    dev: [DataProvider.preload, {
      url: 'bild/client',
      query: { id: landing.params.developer.id },
      requestClientCache: 1
    }],
    childLandings: [DataProvider.preload, {
      url: 'helpers/landings',
      query: { branch: landing.tree },
      requestClientCache: 1
    }],
    projects: [DataProvider.preload, {
      url: 'bild/newbuilds-list',
      queryJson: true,
      query: {
        creator: landing.params.developer.id,
        locality: landing.params.projects ? landing.params.projects.locality : null,
        order: ordersLC()[0].param,
        limit: 500,
        ...query
      },
      requestClientCache: 1
    }],
    ...localityInfo,
    ...seoPreload({
      entity: landing.params.projects ? 'dev-regional' : 'developer'
    }, ['dev', ...Object.keys(localityInfo)]),
    ...simpleCheck(['dev'])
  };
})
@seoDecorator({
  advancedVars: {
    projectsHeader: 'Заголовок H2',
    bottomText: 'Текст в самом низу'
  }
})
@mstatDecorator(marketStatEvents.historyChange, params => ({
  entity: 'Карта застройщика',
  entityId: get(params, 'dev.data[0].id')
}))
@I18nHoc(translates())
export default class PageDeveloper extends AbstractQueryPage {
  static propTypes = {
    className: PropTypes.string,
    params: PropTypes.shape({
      developer: PropTypes.string,
      branchUrl: PropTypes.string,
      id: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
      ])
    }),
    childLandings: PropTypes.shape({}),
    dev: PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape({}))
    }),
    seoData: PropTypes.shape({}),
    projects: PropTypes.shape({}),
    firstCount: PropTypes.number,
    addedCount: PropTypes.number,
  };

  constructor(props, ctx) {
    super(props, ctx);

    this.tileCard = TileCard;
    this.project = 'bild';
    this.needCount = true;

    this.state = {
      isFullScreenMode: false,
      fullMapLegend: false,
      handleMarkerId: null
    };
  }

  componentDidMount() {
    this.needCount = false;
  }

  componentWillUnmount() {
    clearTimeout(this.autoCloseMapLegendDelay);
    clearTimeout(this.closeMapLegendDelay);
  }

  renderNaviRegion() {
    const { i18n } = this;
    const { seoData, params, childLandings, location } = this.props;
    const { query } = location;
    const childTabs = [];
    let topicH2 = seoData.projectsHeader || i18n('developerH2');
    topicH2 = (
      <div {...bemClasses('rowTopic')}>
        <div {...bemClasses('text', 'h2')}><h2 children={topicH2}/></div>
      </div>
    );
    let data = sortBy(childLandings.data, item => item.data.sort);
    let selectedTab = 0;
    let idx = 0;

    // If no objects
    if (!data) return null;
    if (data && data.length === 1) return <Container body children={topicH2} />

    // If builder has only one region - show only one tab with name of this region
    if (data.length === 2) {
      data[0].data.name = data[1].data.name;
      data = [data[0]];
    }
    each(data, (land, key) => {
      if (params.url === land.url) selectedTab = idx;
      idx += 1;
      const name = land.data.name || i18n('developerLand');

      childTabs.push(
        <tab
          key={key}
          title={name}
          linkProps={{
            builder: linksBild.sectionLanding,
            saveScrollState: true,
            builderProps: {
              ...land,
              query,
              name
            }
          }}
        />
      );
    });

    return (
      <>
        <Container body children={topicH2} />
        <div {...bemClasses('tabs', 'main sticky')} data-test="tabsMain">
          <Container body>
            <TabJSControl
              selectedTab={selectedTab}
              linkMode
            >
              {childTabs}
            </TabJSControl>
          </Container>
        </div>
        <Container {...bemClasses('filters')} body data-test="filters">
          <Dict
            url="location/living-complex/build-status-filter"
            injectPropName="listBoxItems"
          >
            <InputListBox
              dictValue="value"
              nullValueLabel={i18n('developerFilterAllProjects')}
              value={get(this, 'props.location.query.bildReadyStatus')}
              onChange={val =>
                this.updateState({ bildReadyStatus: { val } })
              }
              horizontal
              noError
            />
          </Dict>
        </Container>
      </>
    );
  }

  getMapMarkerIcon(feature) {
    return marker(getBigMarkerType.call(null, feature));
  }

  getMapMarkerText(point) {
    return point.properties.min_price || point.properties.price || point.properties.display_name;
  }

  handleCardHover = (obj) => {
    this.showOnMap = obj;
    if (this.hoverLayerRef && obj) {
      this.hoverLayerRef.setData({
        type: 'Feature',
        geometry: obj.geometry || obj.geo,
        properties: {
          points: obj.points,
          living_complex_build_status: obj.living_complex_build_status
        }
      });
    }
  };

  handleCardUnhover = () => {
    this.showOnMap = null;
    if (this.hoverLayerRef) this.hoverLayerRef.setData(null);
  };

  mapMarker = (point, latLng) =>
    Leaflet.marker(latLng, { icon: mapMarkerRenderer.call(null, point) });

  handleMarkerClick=(evt) => {
    const handleMarkerId = get(evt, 'target.feature.properties.id');
    this.setState({ handleMarkerId });
  };

  clickOutsideMapCard = evt => outsideMapCard.call(this, evt);

  mapTileRenderer = payload => mapTile.call(this, payload, ListCard);

  changeSizeMap = () => sizeMap.call(this);

  changeMapLegendView = () => mapLegendView.call(this);

  renderDeveloperMap = () => {
    const query = get(this, 'props.location.query', {});
    const creator = get(this, 'props.dev.data[0].id');
    const locality = get(this, 'props.locality.data[0].id');

    return (
      <>
        <div {...classesSearch('mapContainer')} data-test="mapContainer">
          <Maps
            zoom={12}
            noAutoGeolocate
            adaptiveScrollZoom={2000}
            height="calc(100vh - 6.6rem)"
          >
            <BaseLayersUser positionLayerSwitch="bottomleft" />
            <DataProvider
              url="bild/newbuilds-list-map"
              query={{
                creator,
                limit: 500,
                locality,
                ...query
              }}
              queryJson
            >
              <InfoLayer
                cluster
                spiderfyDistanceMultiplier={1.5}
                maxClusterRadius={50}
                pointToLayer={this.mapMarker}
                showCoverageOnHover={false}
                onFeatureClick={this.handleMarkerClick}
              />
            </DataProvider>
            <InfoLayer
              noAutoFit
              ref={el => this.hoverLayerRef = el}
              onCentralMarker={feature =>
                feature.geometry.type !== 'Point' ? { icon: this.getMapMarkerIcon(feature) } : null
              }
            />
          </Maps>
        </div>
      </>
    );
  };

  renderProjects = (projects) => {
    if (!projects || !projects.data || !projects.data.length) return null;

    const developerMapTile = complexMapTile.call(this);

    return (
      <>
        <Row>
          <Col {...bemClasses('col', 'projects')} allPr={0}>
            <div {...bemClasses('projects')}>
              <FavoriteWrapper
                project="bild"
                entity="location"
              >
                {({ data }) => map(projects.data, (item, key) => (
                  <TileCard
                    key={key}
                    onMouseOver={() => this.handleCardHover(item)}
                    onMouseOut={this.handleCardHover}
                    object={item}
                    favData={data}
                  />
                ))}
              </FavoriteWrapper>
              {renderGeneralMap.call(this, this.renderDeveloperMap, developerMapTile)}
            </div>
          </Col>
        </Row>
      </>
    );
  };

  renderImageAndAvatar(main_photo, avatar, topic) {
    const { i18n } = this;
    const avatarDeveloper = { ...avatar, alt: topic, title: topic };

    return (
      <div {...bemClasses('imgMainContainer')}>
        <Image
          {...bemClasses('imgMain')}
          imgOrigin
          image={{
            id: main_photo,
            renderer: 'orig',
            alt: i18n('developerImgAlt', { placeholder: topic })
          }}
          fullScreenSwitch="none"
        />
        <JsonLd
          item={{
            '@context': 'https://schema.org',
            '@type': 'ImageObject',
            name: topic ? i18n('developerImgAlt', { placeholder: topic }) : null,
            description: topic ? i18n('developerImgAlt', { placeholder: topic }) : null,
            contentUrl: main_photo ? absoluteUrl(`/images/thumb/full/${main_photo}`) : null
          }}
        />
        {avatar &&
          <Container body>
            <Row {...bemClasses('row', 'logo')}>
              <Col allPr={0}>
                <Thumb
                  {...bemClasses('logo')}
                  image={avatarDeveloper}
                />
                <JsonLd
                  item={{
                    '@context': 'https://schema.org',
                    '@type': 'ImageObject',
                    name: get(avatarDeveloper, 'alt', null),
                    description: get(avatarDeveloper, 'title', null),
                    contentUrl: get(avatar, 'id') ? absoluteUrl(`/images/thumb/full/${avatar.id}`) : null
                  }}
                />
              </Col>
            </Row>
          </Container>
        }
      </div>
    );
  }

  renderWebsite(website, titleDeveloper) {
    if (!website) return null;

    return (
      <div {...bemClasses('links')} data-test="links">
        <Link
          {...bemClasses('link')}
          href={website}
          title={this.i18n('developerLinkToSiteTitle')}
          dataProps={{
            'target': titleDeveloper || 'other',
            'place': 'Developer-Page',
            'type': 'dev'
          }}
          external
          blank
        >
          <span {...bemClasses('linkContent')} data-test="linkContent">
            <span {...bemClasses('text')} children={urlShortener(website)} />
          </span>
        </Link>
      </div>
    );
  }

  renderUrlForum(url, title) {
    if (!url) return null;

    return (
      <div {...bemClasses('links', 'forum')}>
        <Link
          {...bemClasses('link')}
          href={url}
          title={title}
          noRedirector
          external
          blank
        >
          <span {...bemClasses('linkContent')}>
            <span {...bemClasses('text')} children={title} />
          </span>
        </Link>
      </div>
    );
  }

  renderPhones =(phones, developer_id)=> {
    if (!phones || !phones.length || !developer_id) return null;

    const { i18n } = this;

    return (
      <div {...bemClasses('links', 'phones')}>
        {map(phones, (phone, key) => (
          <Fragment key={key}>
            <Counter
              action="phoneClick"
              entity={{developer_id}}
              mode="click"
              injectClickName="onClick"
            >
              <HideTelephoneSeo
                {...bemClasses('link', 'telSeo')}
                phone={phone}
                slicedNumber={6}
                btnLabel={'\u00a0xx xx'}
                classMods={{
                  telBild: true
                }}
              />
            </Counter>
            {phones.length !== key + 1 &&
              ',\u00a0'
            }
          </Fragment>
        ))}
      </div>
    );
  }

  renderBildDesc(bild_desc) {
    const { i18n } = this;

    return (
      <Col allPr={0}>
        {bild_desc &&
          <div {...bemClasses('description')}>
            <CollapseLines
              itemClassName={bemClasses('text', 'description').className}
              text={bild_desc}
              moreText={i18n('moreDetails')}
              lessText={i18n('hide')}
              linesConfig={{
                desktop: 5,
                tablet: 4,
                phone: 11
              }}
            />
          </div>
        }
      </Col>
    );
  }

  getCountLC(projects) {
    if (!projects) return null;
    const filteredLC = filter(projects, it => it.type === 'living_complex');

    return filteredLC.length;
  }

  render() {
    const { i18n } = this;
    const {
      className, seoData, projects, dev = {}, params: { landing, url }, mainMenuBase
    } = this.props;
    const { data = [] } = dev;
    const userData = data[0] || {};
    const {
      info = {}, avatar, website, living_complex_counters = [], phones,
      rel_finished_cottage_towns, company_name, id, url_forum, rel_sys
    } = userData;
    const { main_photo, work_start_date, bild_desc } = info;
    const { id: avatarLogo } = avatar;
    const topic = seoData && seoData.header ? seoData.header : company_name;
    const seoText = get(seoData, 'bottomText', null);
    const { isFullScreenMode, fullMapLegend } = this.state;
    const housesCounter = getHousesCounter.call(null, living_complex_counters);
    const minBuiltYear = housesCounter.ready_house_from_year ?
      i18n('developerYear', { placeholder: housesCounter.ready_house_from_year }) : '';
    const countLC = this.getCountLC(get(projects, 'data', []));
    const dev_index_by_lc = get(rel_sys, 'dev_index_by_lc') || {};

    return (
      <div {...bemClasses({ extra: className })}>
        <LazyHydrate ssrOnly>
          <OpenGraphImage imageId={avatarLogo} />
        </LazyHydrate>
        {this.needCount &&
          <Counter
            entity={{
              developer_id: userData.id
            }}
            mode="view"
            action="pageDeveloperView"
          />
        }
        {this.renderImageAndAvatar(main_photo, avatar, topic)}
        <Container body>
          <LazyHydrate whenIdle>
            <Row {...bemClasses('row', 'main')}>
              <Col allPr={0}>
                <div {...bemClasses('title')} data-test="developerTitle">
                  <div {...bemClasses('text')} data-test="developerText"><h1 children={topic} /></div>
                </div>
                {this.renderWebsite(website, topic)}
                {this.renderPhones(phones, id)}
                {this.renderUrlForum(url_forum, i18n('developerForum', { placeholder: company_name }))}
                <Row {...bemClasses('block', 'descriptionDates')} mtColumn mpColumn>
                    {this.renderBildDesc(bild_desc)}
                    <Col allAuto allPr={0}>
                      <Row {...bemClasses('row', 'dates')} allAuto>
                        <Col {...bemClasses('col')} allPr={0}>
                          {work_start_date &&
                            <>
                              <div>
                                <span {...bemClasses('text', 'main')} children={formatDate({ date: work_start_date, format: 'yyyy' })} data-test="textMain" />
                              </div>
                              <div>
                                <span {...bemClasses('text', 'info')} children={i18n('developerFoundedYearText')} />
                              </div>
                            </>
                          }
                        </Col>
                        {!!countLC &&
                          <Col {...bemClasses('col')} allPr={0}>
                            <div>
                              <span
                                {...bemClasses('text', 'main')}
                                children={beautifyNumber(housesCounter.ready_house_number || 0)}
                              />
                            </div>
                            <div>
                              <span {...bemClasses('text', 'info')} children={`${numDescOnlyText(
                                housesCounter.ready_house_number || 0,
                                i18n('developerHouseReady'),
                                i18n('developerHouseReadyDeclination'),
                                i18n('developerHouseReadyMultiple')
                              )} ${i18n('developerInCount', { placeholder: beautifyNumber(housesCounter.ready_house_in_lc_number || 0) })}${minBuiltYear}`}/>
                            </div>
                          </Col>
                        }
                        {+rel_finished_cottage_towns > 0 &&
                          <Col {...bemClasses('col')} allPr={0}>
                            <div>
                              <span
                                {...bemClasses('text', 'main')}
                                children={beautifyNumber(+rel_finished_cottage_towns || 0)}
                              />
                            </div>
                            <div>
                              <span
                                {...bemClasses('text', 'info')}
                                children={`${numDescOnlyText(
                                  +rel_finished_cottage_towns || 0,
                                  i18n('developerHouseReadyCT'),
                                  i18n('developerHouseReadyCTDeclination'),
                                  i18n('developerHouseReadyCTMultiple')
                                )}`}
                              />
                            </div>
                          </Col>
                        }
                        {!!countLC &&
                          <Col {...bemClasses('col')} allPr={0}>
                            <div>
                              <span
                                {...bemClasses('text', 'main')}
                                children={beautifyNumber(housesCounter.in_progress_house_number || 0)}
                              />
                            </div>
                            <div>
                              <span
                                {...bemClasses('text', 'info')}
                                children={
                                  `${numDescOnlyText(
                                    housesCounter.in_progress_house_number || 0,
                                    i18n('developerHouseBuilt'),
                                    i18n('developerHouseBuiltDeclination'),
                                    i18n('developerHouseBuiltMultiple')
                                  )} ${i18n(
                                    'developerInCount',
                                    { placeholder: beautifyNumber(housesCounter.in_progress_house_in_lc_number || 0) }
                                  )}`
                                }
                              />
                            </div>
                          </Col>
                        }
                        {+get(dev_index_by_lc, 'count_lc') >=2 &&
                          <Col {...bemClasses('col', 'reliability')} allPr={0}>
                            <div>
                              <span {...bemClasses('text', 'main')}>
                                <span {...bemClasses('text', 'points')} children={dev_index_by_lc.filled} />
                                <span {...bemClasses('text', 'pointsAll')} children={`${i18n('from')} ${dev_index_by_lc.all}`} />
                              </span>
                            </div>
                            <div>
                              <span
                                {...bemClasses('text', 'info')}
                                children={i18n('middleReliabilityIndex')}
                              />
                              <ToolTip
                                {...bemClasses('tooltip')}
                                titleHtml={
                                  <div
                                    {...bemClasses('tooltipContent')}
                                    children={i18n('middleReliabilityIndexDescription')}
                                  />
                                }
                              >
                                <Icon {...bemClasses('icon', 'tooltipHelp')} type={iconTypes.helpCircle} />
                              </ToolTip>
                            </div>
                          </Col>
                        }
                      </Row>
                    </Col>
                  </Row>
              </Col>
            </Row>
          </LazyHydrate>
        </Container>
        <LazyHydrate whenVisible>
          {contentBlock(blocksConfig, 'userAdvert', this.props, { wrapper: BottomBlockWrapper, withContainer: false, location: get(this, 'props.location') })}
        </LazyHydrate>
        <div {...bemClasses('block', 'projects')} data-test="blockProjects">
          <LazyHydrate whenIdle>
            {this.renderNaviRegion()}
          </LazyHydrate>
          <Container body>
            <DataProvider
              preloaded={projects}
              preloadedStatic
              renderer={this.renderProjects}
              reloadProvider={{ isFullScreenMode, fullMapLegend }}
            />
          </Container>
          <LazyHydrate whenVisible>
            <GeoLandings base={mainMenuBase} injectPropName="landingLinks" project="bild">
              <GeoLandingsWrapper {...this.props} />
            </GeoLandings>
          </LazyHydrate>
        </div>
        <Container body>
          <Breadcrumbs
            data={landing.breadcrumbs}
            replace={{
              $developer: topic
            }}
          />
          <SeoText data={seoText} />
        </Container>
        <JsonLd
          item={{
            '@context': 'https://schema.org',
            '@type': 'HomeAndConstructionBusiness',
            name: topic,
            url: absoluteUrl(url),
            description: bild_desc,
            image: absoluteUrl(`/images/thumb/full/${main_photo}`),
            logo: absoluteUrl(`/images/thumb/full/${avatarLogo}`),
            sameAs: website,
            telephone: [phones],
            foundingDate: work_start_date
          }}
        />
      </div>
    );
  }
}

class GeoLandingsWrapper extends PureComponent {
  static propTypes = {
    landingLinks: PropTypes.shape({}),
    geolocation: PropTypes.shape({})
  };

  render() {
    const { landingLinks, geolocation } = this.props;

    return (
      <>
        {contentBlock(blocksConfig, 'developerBottom1', this.props, { geolocation, landingLinks, wrapper: BottomBlockWrapper, withContainer: false, gtmProps: { 'place': 'Developer-Page' } })}
      </>
    );
  }
}
